import DragDistanceHelper from '../../../tools/drag-distance-helper';
import SectionView from '../../../views/section-view';
import SelectionManager from '../selection-manager';
import Tool from '../../../tools/tool';
import UnitEditorApp from '../unit-editor-app';
import logging from '../../../logging';
import { Section } from '../../../domain';
import { getParentView } from '../../../controllers/base-view-controllers';
import indexRange from '../../../mem/index-range';

const log = logging('tools');

export class SelectTool extends Tool {

    private app: UnitEditorApp;
    private helper: DragDistanceHelper;

    constructor(app: UnitEditorApp, helper: DragDistanceHelper) {
        super();
        this.app = app;
        this.helper = helper;
    }

    enable(): void {
        log.debug('SelectTool.enable()');
        this.helper.on('click', this.begin);
    }

    disable(): void {
        log.debug('SelectTool.disable()');
        this.helper.off('click', this.begin);
    }

    begin(event: PointerEvent): void {
        log.debug('SelectTool.begin()');

        // Note that DragDistanceHelper already filters events to the left
        // mouse button, so we don't need to re-check that here.

        let top = this.app.picker.topHit;
        if(top && top.userData.isSelectable) {
            const newSelection = getParentView(top);
            if(event.shiftKey) {
                if(newSelection instanceof SectionView
                   && isSectionSelection(this.app.selection))
                {
                    // getSelectedSections may return an empty list if the
                    // newly selected section is not a valid extension of the
                    // existing selection.
                    for(const view of getSelectedSections(this.app.selection, newSelection)) {
                        this.app.selection.add(view, view.model);
                    }
                }
            } else {
                // TODO: extend the selection to the parent section if we're
                // clicking on the selection a second time
                this.app.selection.set(newSelection, newSelection.model);
            }
        } else {
            this.app.selection.clear();
        }
    }

    cancel(): void {
        log.debug('SelectTool.cancel()');
        // we have nothing to do
    }

    end(): void {
        log.debug('SelectTool.end()');
        // we have nothing to do
    }
}
export default SelectTool;

/**
 * Check if the current selection consists entirely of sections. If there is
 * no selection at all, this returns true.
 */
const isSectionSelection = (sel: SelectionManager): boolean => {
    if(sel.model && sel.model instanceof Section) {
        return true;
    } else if(sel.multi) {
        for(const item of sel.multi) {
            if(! (item.model instanceof Section)) {
                return false;
            }
        }
        return true;
    } else if(sel.model == null && sel.multi == null) {
        return true;
    }
    return false;
}

/**
 * Assuming that every item already selected is a section, this returns one or
 * more section views that should be added to the selection to form a
 * contiguous selection. The result may be empty if no such selection can be
 * formed.
 */
const getSelectedSections = (sel: SelectionManager, view: SectionView): SectionView[] => {
    // We can only generate a multi-section selection when the parent is a
    // SectionView.
    if(!(view.parent instanceof SectionView)) {
        return [];
    }

    // First we make sure that all the selected sections share the same parent
    const parent: SectionView = view.parent;

    if(sel.view && sel.view.parent != parent) {
        return [];
    } else if(sel.multi) {
        for(const item of sel.multi) {
            if(item.view.parent != parent) {
                return [];
            }
        }
    }

    // Find the minimum and maximum incices of the selection and the clicked
    // section.
    let minIndex = parent.sections.indexOf(view);
    let maxIndex = parent.sections.indexOf(view);
    if(sel.view) {
        const idx = parent.sections.indexOf(sel.view as SectionView);
        minIndex = Math.min(minIndex, idx);
        maxIndex = Math.max(maxIndex, idx);
    }
    else if(sel.multi) {
        const [min, max] = indexRange(sel.multi.map(i => i.view), parent.sections);
        if(min <= max) {
            minIndex = Math.min(minIndex, min);
            maxIndex = Math.max(maxIndex, max);
        } else {
            log.warn(`Expected min <= max for a multi-selection, but got (${min}, ${max})`);
        }
    }

    // Find only those items in the min/max range that aren't already selected
    const result = new Set(parent.sections.slice(minIndex, maxIndex + 1));

    if(sel.view) {
        result.delete(sel.view as SectionView);
    }
    else if(sel.multi) {
        for(const item of sel.multi) {
            result.delete(item.view as SectionView);
        }
    }

    return Array.from(result);
}
